import React, { useEffect, useState } from 'react';
import { Button, Input, Select, notification, Spin } from 'antd';
import { contracts } from '../api';
import { employeeId } from '../../GetRole.utils';
import StepDrawer from '../other/StepDrawer';
import {
  Field,
  EntityChoice,
  ClientSelect,
  ProjectSelect,
  AmountInput,
  Summary,
  entityName,
  apiErrorMessage,
  splitAmount,
  formatAmount,
} from '../other/contractFields';

const STATUS = { draft: 1, pending: 2 };

// the server validator rejects null / empty strings for typed fields, so leave them out
const withoutEmpty = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== ''));

const emptyValues = (prefill) => ({
  seId: prefill?.seId ?? 1,
  clientId: prefill?.clientId ?? null,
  clientLabel: prefill?.clientName ?? prefill?.legalEntityName ?? '',
  projectId: prefill?.projectId ? Number(prefill.projectId) : null,
  projectLabel: prefill?.projectName ?? '',
  sowNumber: prefill?.sowNumber ?? null,
  crNumber: '',
  currency: '₹',
  amount: '',
  extendedDays: '',
});

// Create or edit a change request in a step drawer. CRs have no document.
// contractId + status: edit. prefill: lock client/project/SOW (from the SOW page).
const CrDrawer = ({ open, onClose, contractId, status, prefill }) => {
  const isEdit = !!contractId;
  const [step, setStep] = useState(0);
  const [values, setValues] = useState(emptyValues(prefill));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sowOptions, setSowOptions] = useState([]);

  // same rule as the old CR form: any role may send a new or draft CR for approval
  const canSendForApproval = !isEdit || status === 'draft';
  // editing a draft: only the contracting entity is disabled, every other field stays enabled
  const isDraftEdit = isEdit && status === 'draft';
  const entityLocked = isEdit || !!prefill?.clientId;
  const clientLocked = isDraftEdit ? false : isEdit || !!prefill?.clientId;
  const projectLocked = isDraftEdit ? false : isEdit || !!prefill?.projectId;
  const sowLocked = isDraftEdit ? false : !!prefill?.sowNumber;

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setErrors({});
    setValues(emptyValues(prefill));
    if (!isEdit) return;

    setLoading(true);
    contracts
      .getContractsData(contractId)
      .then((res) => {
        const data = res.data[0]?.contractData || {};
        const { currency, amount } = splitAmount(data.totalAmount);
        setValues({
          ...emptyValues(),
          seId: Number(data.seId),
          clientId: data.client?.clientId ?? null,
          clientLabel: data.client?.legalEntityName ?? '',
          projectId: data.project?.projectId ?? null,
          projectLabel: data.project?.projectName ?? '',
          sowNumber: data.sowNumber || null,
          crNumber: data.crNumber || '',
          currency,
          amount,
          extendedDays: data.crExtendedNoOfDays != null ? String(data.crExtendedNoOfDays) : '',
        });
      })
      .catch((err) => console.error('Error fetching CR:', err))
      .finally(() => setLoading(false));
    // prefill is read once when the drawer opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contractId]);

  useEffect(() => {
    if (!open || !values.projectId) {
      setSowOptions([]);
      return;
    }
    contracts
      .getSowDropDown(values.projectId)
      .then((res) =>
        setSowOptions((res.data || []).map((s) => ({ value: s.sowNumber, label: s.sowNumber })))
      )
      .catch((err) => console.error('Error fetching SOWs:', err));
  }, [open, values.projectId]);

  const update = (patch) => {
    setValues((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((key) => delete next[key]);
      return next;
    });
  };

  const stepErrors = (index) => {
    const e = {};
    if (index === 0) {
      if (!values.clientId) e.clientId = 'Choose a client';
      if (!values.projectId) e.projectId = 'Choose a project';
      if (!values.sowNumber) e.sowNumber = 'Choose the SOW this change applies to';
    }
    if (index === 1) {
      if (!values.crNumber) e.crNumber = 'Enter the CR number';
      if (!values.amount) e.amount = 'Enter the CR amount';
      if (!values.extendedDays) e.extendedDays = 'Enter the number of days added (0 if none)';
    }
    return e;
  };

  const goNext = () => {
    const e = stepErrors(step);
    setErrors(e);
    if (Object.keys(e).length === 0) setStep(step + 1);
  };

  const save = (cStatusId) => {
    if (cStatusId === STATUS.pending) {
      for (let i = 0; i < 2; i++) {
        const e = stepErrors(i);
        if (Object.keys(e).length) {
          setErrors(e);
          setStep(i);
          return;
        }
      }
    }

    const postData = withoutEmpty({
      cStatusId,
      seId: Number(values.seId),
      clientId: values.clientId,
      projectId: values.projectId,
      sowNumber: values.sowNumber ? String(values.sowNumber) : null,
      contractTypeId: 4,
      crNumber: values.crNumber ? String(values.crNumber) : null,
      totalAmount: values.amount ? values.currency + values.amount : null,
      crExtendedNoOfDays: values.extendedDays,
      createdBy: employeeId,
    });

    setSaving(cStatusId === STATUS.pending ? 'approval' : 'draft');
    const request = isEdit
      ? contracts.updateContractsData(contractId, postData)
      : contracts.addContractsData(postData);

    request
      .then((res) => {
        notification.success({
          message: cStatusId === STATUS.pending ? 'CR sent for approval' : res?.message || 'CR saved',
        });
        onClose(true);
      })
      .catch((err) => {
        notification.error({
          message: apiErrorMessage(err, 'Could not save the CR'),
        });
      })
      .finally(() => setSaving(null));
  };

  const steps = [
    {
      key: 'scope',
      title: 'Scope',
      content: loading ? (
        <Spin />
      ) : (
        <div className="sd-form">
          <EntityChoice
            value={values.seId}
            onChange={(seId) =>
              update({ seId, clientId: null, clientLabel: '', projectId: null, projectLabel: '', sowNumber: null })
            }
            disabled={entityLocked}
          />
          <ClientSelect
            seId={values.seId}
            value={values.clientId}
            onChange={(clientId, option) =>
              update({ clientId, clientLabel: option?.label || '', projectId: null, projectLabel: '', sowNumber: null })
            }
            disabled={clientLocked}
            currentOption={values.clientId ? { value: values.clientId, label: values.clientLabel } : null}
            error={errors.clientId}
          />
          <ProjectSelect
            clientId={values.clientId}
            value={values.projectId}
            onChange={(projectId, option) =>
              update({ projectId, projectLabel: option?.label || '', sowNumber: null })
            }
            disabled={projectLocked}
            currentOption={values.projectId ? { value: values.projectId, label: values.projectLabel } : null}
            error={errors.projectId}
          />
          <Field label="SOW" required error={errors.sowNumber}>
            <Select
              size="large"
              placeholder={values.projectId ? 'Choose the SOW this change applies to' : 'Choose a project first'}
              value={values.sowNumber}
              onChange={(sowNumber) => update({ sowNumber })}
              options={
                values.sowNumber && !sowOptions.some((o) => o.value === values.sowNumber)
                  ? [{ value: values.sowNumber, label: values.sowNumber }, ...sowOptions]
                  : sowOptions
              }
              disabled={sowLocked || !values.projectId}
              status={errors.sowNumber ? 'error' : ''}
              notFoundContent="No SOWs for this project"
            />
          </Field>
        </div>
      ),
    },
    {
      key: 'change',
      title: 'Change',
      content: (
        <div className="sd-form">
          <Field label="CR number" required error={errors.crNumber} help="Letters, numbers, - # /">
            <Input
              size="large"
              value={values.crNumber}
              status={errors.crNumber ? 'error' : ''}
              placeholder="e.g. CR-07"
              onChange={(e) =>
                update({ crNumber: e.target.value.replace(/[^a-zA-Z0-9-#/]/g, '').toUpperCase() })
              }
            />
          </Field>
          <div className="sd-row">
            <AmountInput
              label="CR amount"
              currency={values.currency}
              amount={values.amount}
              onChange={update}
              error={errors.amount}
            />
            <Field label="Days added to the SOW" required error={errors.extendedDays}>
              <Input
                size="large"
                inputMode="numeric"
                maxLength={4}
                value={values.extendedDays}
                status={errors.extendedDays ? 'error' : ''}
                placeholder="0"
                suffix="days"
                onChange={(e) => update({ extendedDays: e.target.value.replace(/\D/g, '') })}
              />
            </Field>
          </div>
        </div>
      ),
    },
    {
      key: 'review',
      title: 'Review',
      content: (
        <div className="sd-form">
          <Summary
            rows={[
              { label: 'Contracting entity', value: entityName(values.seId) },
              { label: 'Client', value: values.clientLabel },
              { label: 'Project', value: values.projectLabel },
              { label: 'SOW', value: values.sowNumber },
              { label: 'CR number', value: values.crNumber },
              { label: 'CR amount', value: formatAmount(values.currency, values.amount) },
              {
                label: 'Days added',
                value: values.extendedDays !== '' ? `${values.extendedDays} days` : '',
              },
            ]}
          />
          {canSendForApproval && (
            <span className="sd-help">
              Sending for approval moves the CR to Pending approval. You can also keep it as a draft.
            </span>
          )}
        </div>
      ),
    },
  ];

  const draftLabel = isEdit ? 'Save changes' : 'Save as draft';

  return (
    <StepDrawer
      open={open}
      title={isEdit ? 'Edit change request' : 'New change request'}
      subtitle={[values.clientLabel, values.sowNumber].filter(Boolean).join(' · ') || 'Change request'}
      steps={steps}
      current={step}
      onStepClick={(index) => (index < step ? setStep(index) : goNext())}
      onBack={() => setStep(step - 1)}
      onNext={goNext}
      onClose={() => onClose(false)}
      footerStart={
        canSendForApproval && (
          <Button onClick={() => save(STATUS.draft)} loading={saving === 'draft'} disabled={!!saving}>
            {draftLabel}
          </Button>
        )
      }
      submitButton={
        canSendForApproval ? (
          <Button
            type="primary"
            onClick={() => save(STATUS.pending)}
            loading={saving === 'approval'}
            disabled={!!saving}
          >
            Send for approval
          </Button>
        ) : (
          <Button type="primary" onClick={() => save(STATUS.draft)} loading={saving === 'draft'} disabled={!!saving}>
            {draftLabel}
          </Button>
        )
      }
    />
  );
};

export default CrDrawer;
